import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Check, Circle, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

// Password strength requirements (signup form overhaul — P0 bundle).
// Supabase Auth do projeto não força policy explícita no lado do DB;
// enforcement é client-side aqui (defense-in-depth, não segurança única).
interface PasswordRequirement {
  key: 'length' | 'uppercase' | 'lowercase' | 'number';
  label: string;
  test: (pw: string) => boolean;
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  { key: 'length', label: 'Mínimo 8 caracteres', test: pw => pw.length >= 8 },
  { key: 'uppercase', label: '1 letra maiúscula', test: pw => /[A-Z]/.test(pw) },
  { key: 'lowercase', label: '1 letra minúscula', test: pw => /[a-z]/.test(pw) },
  { key: 'number', label: '1 número', test: pw => /\d/.test(pw) },
];

export const RegisterPage: React.FC = () => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { signUp } = useAuth();

  const requirementsStatus = useMemo(
    () =>
      PASSWORD_REQUIREMENTS.map(r => ({
        key: r.key,
        label: r.label,
        met: r.test(password),
      })),
    [password]
  );

  const allRequirementsMet = requirementsStatus.every(r => r.met);
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const passwordMismatchError =
    confirmPassword.length > 0 && !passwordsMatch
      ? 'As senhas não coincidem'
      : null;
  const canSubmit =
    !loading &&
    email.trim().length > 0 &&
    allRequirementsMet &&
    passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!allRequirementsMet) {
      setError('A senha não atende a todos os requisitos.');
      return;
    }
    if (!passwordsMatch) {
      setError('As senhas não coincidem.');
      return;
    }

    setLoading(true);
    try {
      const result = await signUp(email, password, fullName);
      if (result.error) {
        setError(result.error);
      } else {
        try {
          localStorage.setItem('pending_onboarding', '1');
        } catch {
          // localStorage pode estar indisponível (private mode) — fallback silencioso
        }
        setSuccess(true);
      }
    } catch (err) {
      console.error('Register error:', err);
      setError('Erro inesperado ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-neutral-900/80 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 sm:p-8 text-center">
        <h2 className="text-xl font-semibold text-white mb-2">Conta criada!</h2>
        <p className="text-neutral-400 text-sm mb-6">
          Verifique seu email para confirmar sua conta.
        </p>
        <Link
          to="/auth/login"
          className="inline-block py-3 sm:py-2.5 px-6 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
        >
          Ir para login
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-neutral-900/80 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 sm:p-8">
      <h2 className="text-xl font-semibold text-white mb-1">Criar conta</h2>
      <p className="text-neutral-400 text-sm mb-6">Comece a usar o One Form</p>

      {error && (
        <div
          role="alert"
          className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg p-3 mb-4"
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div>
          <label htmlFor="fullName" className="block text-sm font-medium text-neutral-300 mb-1">
            Nome completo
          </label>
          <input
            id="fullName"
            type="text"
            autoComplete="name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full px-4 py-3 sm:py-2.5 bg-neutral-800/50 border border-neutral-700/50 rounded-lg text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50"
            placeholder="Seu nome"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-neutral-300 mb-1">
            Email
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 sm:py-2.5 bg-neutral-800/50 border border-neutral-700/50 rounded-lg text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50"
            placeholder="seu@email.com"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-neutral-300 mb-1">
            Senha
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-describedby="password-requirements"
              className="w-full pl-4 pr-10 py-3 sm:py-2.5 bg-neutral-800/50 border border-neutral-700/50 rounded-lg text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50"
              placeholder="Crie uma senha forte"
            />
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              className="absolute right-0 sm:right-2 top-1/2 -translate-y-1/2 p-3.5 sm:p-1.5 rounded-md text-neutral-400 hover:text-white hover:bg-neutral-700/50 transition-colors"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {/* Real-time requirements checklist */}
          <ul id="password-requirements" className="mt-2 space-y-1">
            {requirementsStatus.map(r => (
              <li
                key={r.key}
                className={`flex items-center gap-2 text-xs transition-colors ${
                  r.met ? 'text-green-400' : 'text-neutral-500'
                }`}
              >
                {r.met ? (
                  <Check className="w-3.5 h-3.5 shrink-0" />
                ) : (
                  <Circle className="w-3.5 h-3.5 shrink-0" />
                )}
                <span>{r.label}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <label
            htmlFor="confirmPassword"
            className="block text-sm font-medium text-neutral-300 mb-1"
          >
            Confirme a senha
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              required
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              aria-invalid={!!passwordMismatchError}
              aria-describedby={passwordMismatchError ? 'confirm-password-error' : undefined}
              className={`w-full pl-4 pr-10 py-3 sm:py-2.5 bg-neutral-800/50 border rounded-lg text-white placeholder-neutral-500 focus:outline-none focus:ring-2 ${
                passwordMismatchError
                  ? 'border-red-500/50 focus:ring-red-500/50 focus:border-red-500/50'
                  : passwordsMatch
                    ? 'border-green-500/40 focus:ring-primary-500/50 focus:border-primary-500/50'
                    : 'border-neutral-700/50 focus:ring-primary-500/50 focus:border-primary-500/50'
              }`}
              placeholder="Repita a senha"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(v => !v)}
              aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
              className="absolute right-0 sm:right-2 top-1/2 -translate-y-1/2 p-3.5 sm:p-1.5 rounded-md text-neutral-400 hover:text-white hover:bg-neutral-700/50 transition-colors"
            >
              {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {passwordMismatchError && (
            <p
              id="confirm-password-error"
              className="mt-1.5 text-xs text-red-400"
              role="alert"
            >
              {passwordMismatchError}
            </p>
          )}
          {passwordsMatch && (
            <p className="mt-1.5 text-xs text-green-400 flex items-center gap-1.5">
              <Check className="w-3 h-3" /> Senhas coincidem
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full py-3 sm:py-2.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
        >
          {loading ? 'Criando...' : 'Criar conta'}
        </button>
      </form>

      <p className="mt-4 text-center text-sm text-neutral-400">
        Já tem conta?{' '}
        <Link to="/auth/login" className="text-primary-400 hover:text-primary-300 transition-colors">
          Entrar
        </Link>
      </p>
    </div>
  );
};
